/**
 * Unified Report Driver
 *
 * Exports:
 *   ReportDocument       – React component: renders header, stats, children, optional
 *                          driver-generated desktop table, and footer.
 *   printReportDocument  – opens a self-contained new window and triggers print.
 *   downloadReportCsv    – derives a CSV file from the descriptor.
 *   emailReportSummary   – builds a mailto: link.
 *
 * Descriptor shape (see DESCRIPTOR.md note below):
 * {
 *   store: { name: string },
 *   report: {
 *     title: string,
 *     subtitle: string,
 *     counters: string[],    // names of people who completed the counts
 *     createdBy: string,     // user who generated the report
 *     generatedAt: string,   // ISO timestamp
 *     meta: [{ label, value }],  // extra report-specific metadata rows
 *   },
 *   stats:   [{ label, value }],  // optional summary stat cards
 *   sections: [{
 *     title?: string,           // section header (e.g. vendor name)
 *     sectionNote?: string,     // secondary note under the section header
 *     columns: [{
 *       key: string,
 *       label: string,
 *       align: 'text' | 'number' | 'currency',
 *       width?: string,         // e.g. '80px'
 *     }],
 *     rows: [{
 *       _style?: object,        // inline style for row (e.g. color-coded price change)
 *       _rowClass?: string,     // extra CSS class on <tr>
 *       [key: string]: any,     // pre-formatted display strings
 *     }],
 *     totals?: { [key: string]: string },  // footer row
 *   }],
 * }
 */

import React from 'react';

const LOGO_URL = 'https://www.countertop-app.com/icons/Countertop-app_logo_text_only_counter.png';

function esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function csvEsc(value) {
  const s = String(value ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildAllMeta(descriptor) {
  const { report } = descriptor || {};
  const entries = [];
  if (report?.counters && report.counters.length > 0) {
    entries.push({ label: 'Counted By', value: report.counters.join(', ') });
  }
  if (report?.createdBy) {
    entries.push({ label: 'Created By', value: report.createdBy });
  }
  if (report?.countDates && report.countDates.length > 0) {
    entries.push({ label: 'Count Date', value: report.countDates.join(', ') });
  }
  const generatedStr = report?.generatedAt
    ? new Date(report.generatedAt).toLocaleString()
    : new Date().toLocaleString();
  entries.push({ label: 'Generated', value: generatedStr });
  (report?.meta || []).forEach((m) => entries.push(m));
  return entries;
}

function getPrintCss() {
  return `
    * { box-sizing: border-box; }
    body {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      color: #2b1d14;
      margin: 0.65in 0.75in;
      font-size: 12px;
    }
    .rd-header {
      border-bottom: 2px solid #5A4A42;
      padding-bottom: 12px;
      margin-bottom: 14px;
    }
    .rd-store-name {
      font-size: 20px;
      font-weight: 800;
      color: #3c322b;
      letter-spacing: 0.03em;
      margin-bottom: 3px;
    }
    .rd-report-title {
      font-size: 14px;
      color: #5A4A42;
    }
    .rd-meta {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 8px;
      background: #faf8f5;
      border: 1px solid #ece4db;
      border-radius: 6px;
      padding: 8px 10px;
      margin-bottom: 14px;
    }
    .rd-meta-item dt {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #8d8177;
      font-weight: 700;
      margin-bottom: 1px;
    }
    .rd-meta-item dd {
      margin: 0;
      color: #463a32;
      font-weight: 600;
      font-size: 11px;
    }
    .rd-stats {
      display: flex;
      gap: 10px;
      margin-bottom: 14px;
      flex-wrap: wrap;
    }
    .rd-stat {
      border: 1px solid #e9dfd6;
      border-radius: 6px;
      padding: 6px 12px;
      background: #fff;
      min-width: 70px;
    }
    .rd-stat-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #8b7b73;
      font-weight: 600;
    }
    .rd-stat-value {
      font-size: 14px;
      font-weight: 800;
      color: #3D2518;
      margin-top: 1px;
    }
    .rd-section-title {
      font-size: 13px;
      font-weight: 700;
      color: #3D2518;
      margin: 14px 0 3px;
      padding: 4px 8px;
      background: #f0e8df;
      border-radius: 4px;
    }
    .rd-section-note {
      font-size: 10px;
      color: #7f7168;
      margin-bottom: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
      margin-bottom: 12px;
    }
    th {
      background: #f7f1ea;
      padding: 5px 7px;
      text-align: left;
      border: 1px solid #d8cdc3;
      font-weight: 700;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #5A4A42;
    }
    td {
      padding: 4px 7px;
      border: 1px solid #e8e0d8;
    }
    tr:nth-child(odd) td { background: #f7f1ea; }
    tr:nth-child(even) td { background: #fff; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    th.num { text-align: right; }
    .txt { text-align: left; }
    th.txt { text-align: left; }
    tfoot tr td, tfoot tr th {
      background: #d1e7dd !important;
      font-weight: 700;
      border-top: 2px solid #a3c4b5;
    }
    .rd-alert-banner {
      border-radius: 5px;
      padding: 7px 12px;
      margin-bottom: 12px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
    .rd-alert-warning { background: #fff3cd; border: 1.5px solid #ffc107; color: #7a5900; }
    .rd-alert-danger  { background: #f8d7da; border: 1.5px solid #dc3545; color: #7a0017; }
    .rd-alert-info    { background: #d1ecf1; border: 1.5px solid #17a2b8; color: #0c5460; }
    .rd-footer {
      margin-top: 24px;
      border-top: 1px solid #d8cdc3;
      padding-top: 14px;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .rd-footer img { height: 28px; opacity: 0.22; }
    .rd-footer-text { font-size: 10px; color: #6f6159; line-height: 1.6; }
    @media print {
      body { margin: 0.5in; }
      .rd-section-title { break-before: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    }
  `;
}

function buildPrintSectionsHtml(sections) {
  if (!sections || !sections.length) return '';
  return sections.map((section) => {
    const cols = section.columns || [];
    const rows = section.rows || [];
    const titleHtml = section.title
      ? `<div class="rd-section-title">${esc(section.title)}</div>` : '';
    const noteHtml = section.sectionNote
      ? `<div class="rd-section-note">${esc(section.sectionNote)}</div>` : '';
    const theadHtml = `<thead><tr>${cols.map((c) =>
      `<th class="${c.align !== 'text' ? 'num' : 'txt'}"${c.width ? ` style="width:${c.width}"` : ''}>${esc(c.label)}</th>`
    ).join('')}</tr></thead>`;
    const tbodyHtml = `<tbody>${rows.map((row) =>
      `<tr${row._style ? ` style="${Object.entries(row._style || {}).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';')}"` : ''}>
        ${cols.map((c) => `<td class="${c.align !== 'text' ? 'num' : 'txt'}">${esc(row[c.key] ?? '')}</td>`).join('')}
      </tr>`
    ).join('')}</tbody>`;
    let tfootHtml = '';
    if (section.totals) {
      tfootHtml = `<tfoot><tr>${cols.map((c) =>
        `<td class="${c.align !== 'text' ? 'num' : 'txt'}">${esc(section.totals[c.key] ?? '')}</td>`
      ).join('')}</tr></tfoot>`;
    }
    return `${titleHtml}${noteHtml}<table>${theadHtml}${tbodyHtml}${tfootHtml}</table>`;
  }).join('');
}

export function printReportDocument(descriptor) {
  const win = window.open('', '_blank', 'width=960,height=1200');
  if (!win) {
    alert('Pop-up blocked. Allow pop-ups for this site to print reports.');
    return;
  }
  const { store, report, stats, sections } = descriptor || {};
  const storeName = store?.name || '';
  const allMeta = buildAllMeta(descriptor);

  const metaHtml = allMeta.length > 0
    ? `<dl class="rd-meta">${allMeta.map((m) =>
        `<div class="rd-meta-item"><dt>${esc(m.label)}</dt><dd>${esc(m.value)}</dd></div>`
      ).join('')}</dl>`
    : '';

  const statsHtml = (stats && stats.length > 0)
    ? `<div class="rd-stats">${stats.map((s) =>
        `<div class="rd-stat"><div class="rd-stat-label">${esc(s.label)}</div><div class="rd-stat-value">${esc(s.value)}</div></div>`
      ).join('')}</div>`
    : '';

  const title = `${report?.title || 'Report'} — ${report?.subtitle || ''}`;

  const alertBanner = report?.alert
    ? `<div class="rd-alert-banner rd-alert-${esc(report.alert.variant || 'warning')}">${esc(report.alert.text)}</div>`
    : '';

  const footerNote = report?.footer?.note
    || 'Produced with Countertop App — Hospitality inventory that doesn’t take 365 days to set up';
  const footerUrl = report?.footer?.url || 'www.countertop-app.com';

  const html = `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<style>${getPrintCss()}</style>
</head><body onload="window.print()">
  <div class="rd-header">
    <div class="rd-store-name">${esc(storeName)}</div>
    <div class="rd-report-title">${esc(report?.title || '')} &mdash; ${esc(report?.subtitle || '')}</div>
  </div>
  ${alertBanner}
  ${metaHtml}
  ${statsHtml}
  ${buildPrintSectionsHtml(sections)}
  <div class="rd-footer">
    <img src="${LOGO_URL}" alt="Countertop" onerror="this.style.display='none'" />
    <div class="rd-footer-text">
      <div>${esc(footerNote)}</div>
      <div>${esc(footerUrl)}</div>
    </div>
  </div>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

export function downloadReportCsv(descriptor, filename) {
  const { sections, report } = descriptor || {};
  if (!sections || !sections.length) return;
  const lines = [];
  sections.forEach((section, idx) => {
    if (section.title && sections.length > 1) {
      lines.push(csvEsc(section.title));
    }
    const cols = section.columns || [];
    lines.push(cols.map((c) => csvEsc(c.label)).join(','));
    (section.rows || []).forEach((row) => {
      lines.push(cols.map((c) => csvEsc(row[c.key] ?? '')).join(','));
    });
    if (section.totals) {
      lines.push(cols.map((c) => csvEsc(section.totals[c.key] ?? '')).join(','));
    }
    if (idx < sections.length - 1) lines.push('');
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const safeName = (report?.title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  link.download = filename || `${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export function emailReportSummary(descriptor, bodyLines = []) {
  const { store, report } = descriptor || {};
  const subject = `${store?.name || 'Store'} ${report?.title || 'Report'}`;
  const generatedStr = report?.generatedAt
    ? new Date(report.generatedAt).toLocaleString()
    : new Date().toLocaleString();
  const body = [
    `${report?.title || 'Report'} generated ${generatedStr}.`,
    '',
    ...bodyLines,
  ].join('\n');
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const IN_APP_CSS = `
  .rd-shell { border: 1px solid #e8ddd2; }
  .rd-toolbar {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .rd-title-block {
    border-bottom: 1px solid #e8ddd2;
    padding-bottom: 0.85rem;
    margin-bottom: 0.85rem;
  }
  .rd-inapp-store-name {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: #3c322b;
  }
  .rd-inapp-subtitle {
    font-size: 0.92rem;
    color: #6c625a;
    margin-top: 0.2rem;
  }
  .rd-meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.65rem;
    background: #faf8f5;
    border: 1px solid #ece4db;
    border-radius: 10px;
    padding: 0.65rem 0.8rem;
    margin-bottom: 0.85rem;
  }
  .rd-meta-grid-item {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }
  .rd-meta-grid-item span {
    font-size: 0.69rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8d8177;
    font-weight: 600;
  }
  .rd-meta-grid-item strong {
    font-size: 0.86rem;
    color: #463a32;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .rd-inapp-alert {
    border-radius: 8px;
    padding: 0.55rem 0.9rem;
    margin-bottom: 0.85rem;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.01em;
  }
  .rd-inapp-alert-warning { background: #fff3cd; border: 1.5px solid #ffc107; color: #7a5900; }
  .rd-inapp-alert-danger  { background: #f8d7da; border: 1.5px solid #dc3545; color: #7a0017; }
  .rd-inapp-alert-info    { background: #d1ecf1; border: 1.5px solid #17a2b8; color: #0c5460; }
  .rd-stat-card {
    border: 1px solid #e9dfd6;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff;
    height: 100%;
  }
  .rd-stat-label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8b7b73;
  }
  .rd-stat-value {
    font-size: 1.05rem;
    font-weight: 700;
    color: #3D2518;
    margin-top: 0.2rem;
  }
  .rd-section-wrap { margin-bottom: 1.5rem; }
  .rd-section-hdr {
    background: #f0e8df;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 0.88rem;
    font-weight: 700;
    color: #3D2518;
    margin-bottom: 5px;
  }
  .rd-section-sub {
    font-weight: 400;
    font-size: 0.8rem;
    color: #7f7168;
  }
  .rd-tbl-wrap {
    border: 1px solid #e8e0d8;
    border-radius: 10px;
    overflow: auto;
    max-height: 62vh;
  }
  .rd-tbl {
    width: 100%;
    min-width: 500px;
    border-collapse: collapse;
    font-size: 0.81rem;
    table-layout: fixed;
    margin-bottom: 0;
  }
  .rd-tbl thead th {
    background: #f8f5f2;
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 6px 8px;
    border-bottom: 2px solid #d6ccc1;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 700;
    color: #5A4A42;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rd-tbl td {
    padding: 5px 8px;
    border-bottom: 1px solid #ede5dd;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
  @media (max-width: 767px) {
    .rd-tbl {
      table-layout: auto;
      font-size: 0.78rem;
    }
    .rd-tbl thead th {
      white-space: nowrap;
      overflow: visible;
      text-overflow: clip;
    }
    .rd-tbl td.rd-txt {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      min-width: 120px;
    }
    .rd-tbl td.rd-num {
      white-space: nowrap;
    }
  }
  .rd-tbl tbody tr:nth-child(odd) td { background: #f7f1ea; }
  .rd-tbl tbody tr:nth-child(even) td { background: #fff; }
  .rd-tbl tfoot td {
    background: #d1e7dd !important;
    font-weight: 700;
    border-top: 2px solid #a3c4b5;
  }
  .rd-num {
    text-align: right !important;
    font-variant-numeric: tabular-nums;
  }
  .rd-tbl thead th.rd-num { text-align: right !important; }
  .rd-txt { text-align: left !important; }
  .rd-tbl thead th.rd-txt { text-align: left !important; }
  .rd-footer-block {
    margin-top: 1rem;
    padding-top: 0.8rem;
    border-top: 1px solid #ece3db;
    text-align: center;
    color: #887d72;
    font-size: 0.78rem;
  }
  .rd-footer-logo {
    width: 110px;
    opacity: 0.2;
  }
`;

export const ReportDocument = ({
  descriptor,
  onDownload,
  onPrint,
  onEmail,
  renderTable = true,
  children,
}) => {
  const { store, report, stats, sections } = descriptor || {};
  const storeName = store?.name || '';
  const allMeta = buildAllMeta(descriptor);

  const handlePrint = onPrint || (() => printReportDocument(descriptor));
  const handleDownload = onDownload;
  const handleEmail = onEmail;

  return (
    <section className="card mb-3 rd-shell">
      <div className="card-body">
        <div className="rd-toolbar">
          {handleDownload && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleDownload}>
              Download
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handlePrint}>
            Print
          </button>
          {handleEmail && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleEmail}>
              Email
            </button>
          )}
        </div>

        <header className="rd-title-block">
          <div className="rd-inapp-store-name">{storeName}</div>
          <div className="rd-inapp-subtitle">
            {report?.title}{report?.subtitle ? ` \u2014 ${report.subtitle}` : ''}
          </div>
        </header>

        {report?.alert && (
          <div className={`rd-inapp-alert rd-inapp-alert-${report.alert.variant || 'warning'}`}>
            {report.alert.text}
          </div>
        )}

        {allMeta.length > 0 && (
          <div className="rd-meta-grid">
            {allMeta.map((entry, idx) => (
              <div className="rd-meta-grid-item" key={`${entry.label}-${idx}`}>
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
              </div>
            ))}
          </div>
        )}

        {stats && stats.length > 0 && (
          <div className="row d-none d-md-flex">
            {stats.map((s) => (
              <div key={s.label} className="col-md-3 col-sm-6 mb-3">
                <div className="rd-stat-card">
                  <div className="rd-stat-label">{s.label}</div>
                  <div className="rd-stat-value">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {children}

        {renderTable && sections && sections.length > 0 && (
          <div>
            {sections.map((section, sIdx) => (
              <div key={sIdx} className="rd-section-wrap">
                {section.title && (
                  <div className="rd-section-hdr">
                    {section.title}
                    {section.sectionNote && (
                      <span className="rd-section-sub"> &mdash; {section.sectionNote}</span>
                    )}
                  </div>
                )}
                <div className="rd-tbl-wrap">
                  <table className="rd-tbl">
                    <thead>
                      <tr>
                        {(section.columns || []).map((col) => (
                          <th
                            key={col.key}
                            className={col.align !== 'text' ? 'rd-num' : 'rd-txt'}
                            style={col.width ? { width: col.width } : undefined}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(section.rows || []).length === 0 ? (
                        <tr>
                          <td
                            colSpan={Math.max(1, (section.columns || []).length)}
                            className="text-center text-muted py-3"
                          >
                            No data.
                          </td>
                        </tr>
                      ) : (
                        (section.rows || []).map((row, rIdx) => (
                          <tr
                            key={rIdx}
                            className={row._rowClass || ''}
                            style={row._style || undefined}
                          >
                            {(section.columns || []).map((col) => (
                              <td
                                key={col.key}
                                className={col.align !== 'text' ? 'rd-num' : 'rd-txt'}
                                title={String(row[col.key] ?? '')}
                              >
                                {row[col.key] ?? ''}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                    {section.totals && (
                      <tfoot>
                        <tr>
                          {(section.columns || []).map((col) => (
                            <td
                              key={col.key}
                              className={col.align !== 'text' ? 'rd-num' : 'rd-txt'}
                            >
                              {section.totals[col.key] ?? ''}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        <footer className="rd-footer-block">
          <p className="mb-1">
            {report?.footer?.note
              || 'Produced with Countertop App — Hospitality inventory that doesn’t take 365 days to set up'}
          </p>
          <p className="mb-2">{report?.footer?.url || 'www.countertop-app.com'}</p>
          <img src="/icons/Countertop-app_logo_text_only_counter.png" alt="Countertop logo" className="rd-footer-logo" />
        </footer>
      </div>

      <style>{IN_APP_CSS}</style>
    </section>
  );
};

export default ReportDocument;
